import { resolveHostContext, ID } from './hostContext';
import { isAdminOnlyAction } from '../config/nonAdminChrome';
/*
 * ⚠ THE PRODUCT AXIS IS A MANIFEST NOW, and nothing here reads a deny list.
 *
 * `isActionAvailableForProfile` and `isActionAvailableForProduct` were `PROFILE_DENIES` and
 * `PRODUCT_DENIES`. Both are gone — see step 3's note for where the profile rules went, and
 * `isToolAvailableForProduct` for why the replacement had to land in the same edit rather than
 * after it.
 */
import { isToolAvailableForProduct } from '../config/manifests';
import { ACTIVE_PRODUCT } from '../config/productTarget';

/**
 * THE COMMAND GATE — three axes, one answer, before anything is dispatched.
 *
 * <p>Open items register, items 1 and 2. `hostContext.js` existed with no reader; this is its
 * first one. The three axes already existed as three unrelated predicates and nothing composed
 * them, so every command reaching the dispatcher was ungated.
 *
 * <pre>
 *   HOST     which product's domain is this                   hostContext.js
 *   ROLE     is the caller an administrator                   nonAdminChrome.js
 *   PROFILE  which toolbar profile is selected                toolbarProfiles.js
 * </pre>
 *
 * <h2>⚠ THE PROFILE AXIS ASKS "AVAILABLE", NOT "VISIBLE"</h2>
 *
 * `isActionVisibleForProfile` is false for every RELOCATED tool, because those were moved off
 * the toolbar into the Admin Panel — and that includes all three engine runners. Using it here
 * would refuse `engine.run:PTA` for an administrator on integralDelta.ai, which is the one
 * configuration it exists for. `isActionAvailableForProfile` is the same deny lists without the
 * toolbar-layout one; the two are documented against each other where they live.
 *
 * <h2>⚠ AND THE DENY-LISTS MEAN AN UNKNOWN ACTION IS ALLOWED</h2>
 *
 * Both predicates answer true for an action they have never heard of — deliberately, and
 * documented as such: a new control invisible to two thirds of users is a bug that looks like an
 * absence. The consequence for a GATE is the opposite of harmless: a command mapped to an action
 * id that does not exist is permitted for everybody, silently.
 *
 * <p>That is not hypothetical. Two of the commands specified for this gate map to
 * `open_eclipse_study_dialog` and `open_pivot_commonality_study_dialog`, and NEITHER EXISTS —
 * both studies are reached by direct callback from the Admin Panel and have no toolbar action at
 * all. Routed through the deny lists they would have been allowed for a customer. They are in
 * {@link ADMIN_ONLY_COMMANDS} instead, which is where a command with no toolbar action to
 * inherit a role from says so. Adding them to `ADMIN_ONLY_TOOLBAR_ACTIONS` would have been
 * wrong for the reason that file already records: that list would then describe a toolbar
 * layout which does not exist.
 *
 * <h2>Unmapped commands are ALLOWED, which is a decision rather than an oversight</h2>
 *
 * Chart, overlay and navigation commands have no entry and pass. That follows the convention
 * every gate in this codebase uses, and it carries the standing cost: a future command is
 * permitted until somebody maps it. {@link ADMIN_ONLY_COMMANDS} is the escape for one that must
 * not be, and `commandGate.test.js` asserts every mapped action id actually exists in the
 * dispatcher — because a typo there fails OPEN.
 */

/* ── the kind:target → toolbar action map ───────────────────────────────────────────────────── */

/**
 * ⚠ EVERY VALUE HERE IS A REAL ACTION IN `actionDispatcher.js`, and a test proves it.
 *
 * <p>A mistyped id is not a broken mapping, it is an OPEN gate: the deny lists have never heard
 * of it, so both axes answer "allowed". Note `open_strategy_2_design_dialog` carries an
 * underscore before the 2 — the obvious `open_strategy2_design_dialog` is not an action, and
 * would have silently permitted designer 2 to everybody.
 */
export const COMMAND_ACTIONS = Object.freeze({
  // The three behavioural-geometry engines.
  'engine.run:PTA': 'run_engine_pta',
  'engine.run:PTB': 'run_engine_ptb',
  'engine.run:CTS': 'run_engine_cts',

  // The two cycle-band presets.
  'engine.runCycleBand:long': 'run_cycle_long',
  'engine.runCycleBand:short': 'run_cycle_short',

  // The four Strategy Designers, reachable as a run or as a dialog open.
  'strategy.run:1': 'open_strategy_design_dialog',
  'strategy.run:2': 'open_strategy_2_design_dialog',
  'strategy.run:3': 'open_time_and_event_strategy_dialog',
  'strategy.run:4': 'open_moa_strategy_dialog',
  'dialog.open:strategyDesign': 'open_strategy_design_dialog',
  'dialog.open:strategy2Design': 'open_strategy_2_design_dialog',
  'dialog.open:timeAndEventStrategy': 'open_time_and_event_strategy_dialog',
  'dialog.open:moa': 'open_moa_strategy_dialog',
});

/**
 * Commands that REQUIRE an administrator and have no toolbar action to inherit that from.
 *
 * <p>Both are Admin Panel tools reached by a direct callback — `onRunEclipseStudy` and
 * `onOpenPivotCommonalities` — so there is no action id, and inventing one would mean inventing
 * a dispatcher case and a handler nobody asked for. Without this set the deny lists would answer
 * "allowed" for both, which is the wrong direction for a mistake in a security gate.
 */
export const ADMIN_ONLY_COMMANDS = Object.freeze([
  'study.run:eclipse',
  'study.run:pivotCommonalities',
  'dialog.open:pivotCommonalities',
]);

/**
 * The four Strategy Designer dialog ids.
 *
 * <p>⚠ NOT a `startsWith('strategy')` test. Two of the four — `timeAndEventStrategy` and `moa` —
 * do not begin with it, so a prefix rule leaves half the designers reachable on a host that is
 * supposed to refuse all of them. A set cannot miss two of four.
 */
export const STRATEGY_DESIGNER_DIALOGS = Object.freeze([
  'strategyDesign', 'strategy2Design', 'timeAndEventStrategy', 'moa',
]);

/**
 * CAPABILITIES A HOST DOES NOT CARRY, keyed by `kind:target` rather than by kind.
 *
 * <h2>⚠ HOST_RESTRICTED IS FOR ABSENCE, NOT FOR POLICY</h2>
 *
 * This used to be two kind-level lists, and a kind is too coarse to say what a host carries.
 * `engine.run` was ID-only in its entirety, so `engine.run:CTS` was refused on AstroStockCharts
 * with "exclusive to integralDelta.ai" — while `run_engine_cts` is live in the ASC profile and
 * reachable from the Admin Panel's Engines group. Two configurations disagreed about the same
 * capability and the hardcoded one won, which is the wrong half to let win: the toolbar config
 * is the thing a reader can inspect and change.
 *
 * <p>So the rule is narrowed to what the refusal actually claims. A key here means the host has
 * no presence and no handler for that capability; everything else is decided by ROLE and
 * PROFILE, which is where access policy already lives.
 *
 * <h2>⚠ CTS IS DELIBERATELY ABSENT, and PTA/PTB are deliberately present</h2>
 *
 * Critical Timing Scan is carried on both products. PTA and PTB are the behavioural-geometry
 * engines and remain integralDelta.ai's alone. Expressing that per target is the whole point of
 * the change — under the old kind rule there was no way to say it.
 *
 * <p>⚠ The backend prunes tools per host too ({@code CopilotTools.ID_ONLY}), and a tool omitted
 * from the payload is one Claude cannot call however this gate answers. The two must agree or
 * relaxing this one is invisible through the Ctrl+K bar; `commandGate.crosslang.test.js` reads
 * the Java and fails when they drift.
 */
export const HOST_EXCLUSIVE = Object.freeze({
  'engine.run:PTA': ID,
  'engine.run:PTB': ID,
  'engine.runCycleBand:long': ID,
  'engine.runCycleBand:short': ID,
});

/** Command kinds the ID host does not carry: astrological research and the designers. */
export const ASC_ONLY_KINDS = Object.freeze(['strategy.run', 'study.run']);

export const REFUSAL = Object.freeze({
  HOST_RESTRICTED: 'HOST_RESTRICTED',
  ROLE: 'ROLE',
  PROFILE: 'PROFILE',
  /**
   * The build target does not carry this control — see `PRODUCT_DENIES`.
   *
   * <p>Distinct from HOST_RESTRICTED, which is about the DOMAIN and about a capability with no
   * handler at all. A reader who sees PRODUCT has asked for something this build deliberately
   * omits; one who sees HOST_RESTRICTED has asked for something that does not exist here. The
   * two deserve different sentences because they have different answers — switch product, or
   * stop asking.
   */
  PRODUCT: 'PRODUCT',
  /** A gated kind arrived with no resolvable target — see GATED_KINDS. */
  INVALID: 'INVALID',
});

const ALLOWED = Object.freeze({ ok: true });
const refuse = (code, message) => ({ ok: false, refusal: { code, message } });

/**
 * Which payload field carries a kind's gate target.
 *
 * <h2>⚠ THE GATE DERIVES THE TARGET ITSELF rather than trusting the producer</h2>
 *
 * The backend normalises `target` onto every command it emits, and relying on that would make
 * the gate's correctness a property of a REMOTE caller. Found by my own test: a perfectly
 * well-formed `{kind:'engine.run', payload:{provider:'PTA'}}` was refused as targetless,
 * because `commandKey` only looked at `target` and `payload.dialog`. A command hand-written in
 * a console, replayed from a log, or sent by a client that has not caught up would fail the
 * same way — or, before CLOSED_KINDS, pass as ungated.
 *
 * <p>`CopilotTest.everyGatedKindHasATargetRule` pins the backend's identical map, so the two
 * cannot disagree about where a target lives.
 */
export const TARGET_FIELDS = Object.freeze({
  'engine.run': 'provider',
  'engine.runCycleBand': 'cycle',
  'strategy.run': 'designer',
  'study.run': 'study',
  'dialog.open': 'dialog',
});

/** `kind:target`, the key both maps are written in. */
export function commandKey(cmd) {
  if (!cmd || typeof cmd.kind !== 'string') return null;
  const field = TARGET_FIELDS[cmd.kind];
  const target = cmd.target
    ?? (field ? cmd.payload?.[field] : undefined)
    ?? cmd.payload?.dialog
    ?? cmd.payload?.target
    ?? null;
  return target === null || target === undefined ? cmd.kind : `${cmd.kind}:${target}`;
}

/** The toolbar action a command maps to, or null when it is ungated. */
export function resolveActionForCommand(cmd) {
  const key = commandKey(cmd);
  if (!key) return null;
  return COMMAND_ACTIONS[key] ?? COMMAND_ACTIONS[cmd.kind] ?? null;
}

/**
 * Kinds for which the map is EXHAUSTIVE — every valid target of these is in `COMMAND_ACTIONS`.
 *
 * <h2>⚠ FOUND WHILE BUILDING THE COPILOT: a missing target was an open gate</h2>
 *
 * `engine.run` with no provider keys on the bare `engine.run`, which is in no map — so
 * `resolveActionForCommand` returns null, the command reads as ungated, and a non-admin on
 * integralDelta.ai is ALLOWED to run an engine. The deny-lists permit what they do not
 * recognise, so a MALFORMED command was the one shape that slipped past every axis.
 *
 * <h2>And `dialog.open` is deliberately NOT here</h2>
 *
 * There are forty-five dialogs and four of them are gated. Closing that kind would refuse
 * `dialog.open:configuration` — an ordinary ungated dialog — as invalid, which is the opposite
 * mistake and a far more visible one. So the refusal is scoped to the kinds whose target set is
 * genuinely finite, and `dialog.open` keeps the deny-list convention: unlisted means ungated.
 */
export const CLOSED_KINDS = Object.freeze(['engine.run', 'engine.runCycleBand', 'strategy.run']);

/** The targets a closed kind accepts, for the refusal message. */
export const targetsFor = (kind) => Object.keys(COMMAND_ACTIONS)
  .filter(k => k.startsWith(`${kind}:`))
  .map(k => k.slice(kind.length + 1));

/** Whether a command opens one of the four Strategy Designers. */
function isStrategyDesignerDialog(cmd) {
  return cmd?.kind === 'dialog.open'
    && STRATEGY_DESIGNER_DIALOGS.includes(cmd.payload?.dialog ?? cmd.target);
}

/**
 * HOST, then ROLE, then PROFILE, then PRODUCT.
 *
 * <p>The order is the one a reader can act on. A customer on the wrong domain cannot fix their
 * role, so telling them about the host first is the actionable refusal — the same reasoning
 * `AreaHighlightAccessGuard` states for answering WHO before WHAT.
 *
 * @param cmd     `{ kind, target?, payload? }`
 * @param context `{ host?, isAdmin?, profile?, product? }` — each falls back to the live
 *                environment
 * @returns `{ ok: true }` or `{ ok: false, refusal: { code, message } }`
 */
export function gate(cmd, context = {}) {
  const host = context.host || resolveHostContext();
  const isAdmin = context.isAdmin ?? false;

  // ── 1. HOST ────────────────────────────────────────────────────────────────────────────────
  // Only an ABSENT capability is refused here. Anything the toolbar config knows about is left
  // to ROLE and PRODUCT below -- see HOST_EXCLUSIVE. (It read "ROLE and PROFILE" until the
  // profile step was retired; the rules it named moved onto the product axis.)
  const exclusiveTo = HOST_EXCLUSIVE[commandKey(cmd)];
  if (exclusiveTo && host !== exclusiveTo) {
    return refuse(REFUSAL.HOST_RESTRICTED,
      `${commandKey(cmd)} has no handler on this product — it is exclusive to `
      + `${exclusiveTo === ID ? 'integralDelta.ai' : 'AstroStockCharts'}.`);
  }
  if (host === ID && (ASC_ONLY_KINDS.includes(cmd?.kind) || isStrategyDesignerDialog(cmd))) {
    return refuse(REFUSAL.HOST_RESTRICTED,
      'Astrological research studies and Strategy Designers are exclusive to AstroStockCharts.');
  }

  // ── 2. ROLE ────────────────────────────────────────────────────────────────────────────────
  const key = commandKey(cmd);
  if (ADMIN_ONLY_COMMANDS.includes(key) && !isAdmin) {
    return refuse(REFUSAL.ROLE, `${key} requires administrator privileges.`);
  }

  const action = resolveActionForCommand(cmd);
  if (!action) {
    // ⚠ A CLOSED KIND WITH NO RESOLVABLE TARGET IS REFUSED, not waved through. See CLOSED_KINDS:
    // an unrecognised action is ALLOWED by both deny-lists, so a malformed engine command was
    // the one shape that passed every axis. Refusing is also right on its own terms -- there is
    // no such command -- and it names the targets, so the caller can correct it.
    if (CLOSED_KINDS.includes(cmd?.kind)) {
      return refuse(REFUSAL.INVALID,
        `${cmd.kind} needs one of: ${targetsFor(cmd.kind).join(', ')}.`);
    }
    return ALLOWED;                   // chart, overlay and navigation commands
  }

  if (isAdminOnlyAction(action) && !isAdmin) {
    return refuse(REFUSAL.ROLE, `${action} requires administrator privileges.`);
  }

  /*
   * ── 3. PROFILE — RETIRED 2026-11-01 ─────────────────────────────────────────────────────────
   *
   * ⚠ REMOVED BECAUSE ITS CONTENT MOVED, not because it was wrong.
   *
   * `PROFILE_DENIES` held two real rules: ASC withheld the AI-analysis and TrendPulse actions,
   * AI withheld the deep astro studies. Both are now PRODUCT facts — the astro manifest does not
   * name Chart Analytica or the Market Scanner, and integralDelta names no planetary study — so
   * step 4 below refuses exactly the same set, and refuses it for EVERYONE rather than only for a
   * developer who had flipped an admin-only dropdown.
   *
   * <p>Its third rule, `ADMIN_ONLY_ACTIONS`, is still enforced — by the ROLE step above, which is
   * the axis it belonged on all along.
   *
   * <p>`REFUSAL.PROFILE` is kept in the enum: it is part of the gate's published contract and a
   * caller may still be branching on it, and an unreachable value costs nothing where a removed
   * one is a breaking change for no benefit.
   */

  // ── 4. PRODUCT ─────────────────────────────────────────────────────────────────────────────
  //
  // ⚠ A FOURTH AXIS, AND THE ONLY ONE THAT IS PER DEPLOYMENT. The HOST step above is close but
  // not the same question: it refuses a capability the product has no HANDLER for, keyed on the
  // domain, and its kind-level rule (`ASC_ONLY_KINDS`) already covers `study.run` and
  // `strategy.run` on the ID host. This step is keyed on the BUILD TARGET and works by ACTION
  // ID, so ONE MANIFEST governs the toolbar and the Copilot together.
  //
  // ⚠ THAT SENTENCE READ "one deny list" UNTIL 2026-11-01, and it had quietly stopped being true:
  // the toolbar became manifest-driven in Stage 1 while this step still read `PRODUCT_DENIES`, so
  // the two could disagree about any tool added afterwards. `isToolAvailableForProduct` asks the
  // same manifest the bar renders from, which is what restores the property the note claims.
  //
  // That matters because the two were able to disagree: the host rule is by KIND, so
  // `dialog.open:aspectStudy` on an integralDelta build passed it — the kind is `dialog.open`,
  // which is not ASC-only — while the toolbar hid the very dialog it opens. A command that
  // succeeds in opening a dialog the product does not carry is the worst of the three outcomes.
  //
  // LAST, because it is the least actionable refusal of the four. A reader can change their
  // profile and an administrator can change their role; nobody can change which product a build
  // is from inside it. The same reasoning the HOST-first ordering states, applied to the end.
  const product = context.product || ACTIVE_PRODUCT;
  if (!isToolAvailableForProduct(action, product)) {
    return refuse(REFUSAL.PRODUCT,
      `${action} is not part of the ${product} product.`);
  }

  return ALLOWED;
}

export default gate;
